import React, { cloneElement, Fragment, ReactElement, useEffect } from "react";

import { CssBaseline, Toolbar, useScrollTrigger } from "@mui/material";
import { useHistory } from "react-router-dom";

import { Routes } from "../../../util/enums";
import { GlobalHeader } from "../../Generic/Header";
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from "Constants";
import { useAppState } from "../../../context/StateContext";
import { Header } from "../NewHeader";
import DumontSidebar from "../../DumontSidebar";
import { isMoving, isProductionShipeeziTestCompany } from "Util/validation";
import { useDumontPanel } from "../../../context/DumontPanelContext";
import FeatureLocked from "Components/FeatureLocked";
import env from "Util/env";

interface Props {
  window?: () => Window;
  children: ReactElement;
}

const ElevationScroll = (props: Props) => {
  const { children, window } = props;

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });

  return cloneElement(children, {
    elevation: trigger ? 4 : 0,
  });
};

export const ElevationHeader = (props: Props & { sidebarPinned: boolean }) => {
  const history = useHistory();
  const { isDumontSidebarOpen, setIsDumontSidebarOpen } = useAppState();
  const { isPanelOpen, openPanel, closePanel } = useDumontPanel();

  // Atalho de teclado global: ⌘+K (Mac) ou Ctrl+K (Windows/Linux)
  useEffect(() => {
    const handleKeyboardShortcut = (event: KeyboardEvent) => {
      // Detecta ⌘+K no Mac ou Ctrl+K no Windows/Linux
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        const isDumontRoute = history.location.pathname
          .toLowerCase()
          .startsWith(PRIVATE_ROUTES.DUMONT_AI.toLowerCase());

        if (isDumontRoute) {
          return;
        }

        event.preventDefault(); // Previne o comportamento padrão do navegador

        if (isPanelOpen) {
          closePanel();
          return;
        }

        openPanel();
      }
    };

    document.addEventListener("keydown", handleKeyboardShortcut);
    return () => document.removeEventListener("keydown", handleKeyboardShortcut);
  }, [history, isPanelOpen, openPanel, closePanel]);
  const { currentUser } = useAppState();

  const renderHeader = () => {
    if (
      !history.location.pathname.includes("videocall") &&
      !history.location.pathname.includes("approve") &&
      !history.location.pathname.includes(Routes.ACCEPT_INVITE) &&
      !history.location.pathname.includes(PUBLIC_ROUTES.SHIPMENT_TRACKING_CLIENT) &&
      !history.location.pathname.includes(Routes.ESTIMATE_QUOTE) &&
      !history.location.pathname.includes(Routes.CUSTOMER_PORTAL) &&
      !history.location.pathname.includes(PUBLIC_ROUTES.ACCEPT_SHIPEEZI_NETWORK_INVITE) &&
      !history.location.pathname.includes(PUBLIC_ROUTES.REJECT_SHIPEEZI_NETWORK_INVITE)
    ) {
      return (
        <>
          <ElevationScroll {...props}>
            {isMoving(currentUser?.company) ? (
              <Header sidebarPinned={props.sidebarPinned} />
            ) : (
              <GlobalHeader />
            )}
          </ElevationScroll>
          <Toolbar />
        </>
      );
    }

    return null;
  };

  return (
    <Fragment>
      <CssBaseline />
      {renderHeader()}
      {props.children}
      {env.NODE_ENV === "production" &&
      !isProductionShipeeziTestCompany(currentUser?.company?.id ?? 0) &&
      isDumontSidebarOpen ? (
        <FeatureLocked featureLabel="Dumont AI" variant="sidebar" />
      ) : (
        <DumontSidebar isOpen={isDumontSidebarOpen} onClose={() => setIsDumontSidebarOpen(false)} />
      )}
    </Fragment>
  );
};
